import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './settings-dialog.css';

interface SettingsDialogProps {
  isOpen: boolean;
  currentUser: string;
  onClose: () => void;
  onSaveUser: (userName: string) => void;
}

/** Tab, Shift+Tab and Escape have to keep working inside a modal. */
const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

const SettingsDialog: React.FC<SettingsDialogProps> = ({
  isOpen,
  currentUser,
  onClose,
  onSaveUser,
}) => {
  const [userName, setUserName] = useState(currentUser);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /* Whatever opened the dialog gets the focus back when it closes, so a
     keyboard user is not dumped at the top of the document. */
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (isOpen) setUserName(currentUser);
  }, [isOpen, currentUser]);

  useEffect(() => {
    if (!isOpen) return undefined;
    openerRef.current = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    inputRef.current?.select();
    return () => {
      const opener = openerRef.current;
      /* Only worth restoring to a real, still-mounted control. Menus in this
         app leave focus on <body> when they close, and focusing <body> back
         is the same as doing nothing — the caller restores in that case. */
      if (opener && opener.isConnected && opener !== document.body) opener.focus();
    };
  }, [isOpen]);

  /* The page behind a modal must not scroll away under it. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const handleSave = useCallback(() => {
    if (!userName.trim()) return;
    onSaveUser(userName.trim());
    onClose();
  }, [userName, onSaveUser, onClose]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = Array.from(
      surfaceRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || [],
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  /* Portalled to <body> on purpose. The rail that opens this dialog is
     `position: sticky`, which always forms a stacking context — rendered in
     place, the overlay is trapped inside it and the page content, painted
     later in DOM order, covers the modal at ANY z-index. */
  return createPortal(
    <div
      className="pnid-modal-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        className="pnid-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={surfaceRef}
      >
        <div className="pnid-modal__head">
          <h2 className="pnid-modal__title" id={titleId}>
            My Profile
          </h2>
          <button
            type="button"
            className="pnid-modal__close"
            onClick={onClose}
            aria-label="Close my profile"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="pnid-modal__body">
          <label className="pnid-modal__label" htmlFor={`${titleId}-user`}>
            Username
          </label>
          <input
            id={`${titleId}-user`}
            ref={inputRef}
            className="pnid-field"
            type="text"
            value={userName}
            onChange={(event) => setUserName(event.target.value)}
            placeholder="Enter your username"
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSave();
            }}
          />
          <p className="pnid-modal__hint">
            Marks and comments you make are attributed to this name.
          </p>
          {currentUser && (
            <p className="pnid-modal__current">
              Signed in as <strong>{currentUser}</strong>
            </p>
          )}
        </div>

        <div className="pnid-modal__foot">
          <button type="button" className="pnid-btn pnid-btn--sm pnid-btn--outlined" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="pnid-btn pnid-btn--sm pnid-btn--contained"
            disabled={!userName.trim()}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SettingsDialog;
